<template>
  <div>
    <v-flex d-flex>
      <v-subheader class="font-weight-bold">
        <span id="environments__rows">
          <span id="environments__total-string">Total: </span>
          <span id="environments__rows-count">{{ rowsCount }}</span>
        </span>
        <v-btn small color="primary" class="ml-2 mr-2" @click="refresh">
          <v-icon small left>mdi-arrow-up</v-icon>
          {{ $t("modules.logs.top") }}
        </v-btn>
      </v-subheader>

      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        :label="$t('actions.search')"
        class="mx-2"
        prepend-icon="mdi-magnify"
        single-line
        clearable
      ></v-text-field>
    </v-flex>
    <v-data-table
      ref="dataContainer"
      :headers="headers"
      :items="loadItems"
      class="elevation-1 mx-2"
      :loading="loading"
      hide-default-footer
      :options.sync="options"
      id="environments__table"
      must-sort
      fixed-header
      :height="height - 15 + 'px'"
    >
      <template v-slot:body.append>
        <td
          colspan="2"
          class="text-center py-2"
          v-if="options.itemsPerPage < loadItems.length"
        >
          <v-progress-circular
            :size="30"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </td>
      </template>
      <template v-slot:item.action="props">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              class="mr-6"
              @click="editItem(props.item)"
              :id="'environments__edit-' + props.index"
              :disabled="!isPermitted(PermissionEnum.ENVIRONMENTS.UPDATE)"
              v-on="on"
              v-bind="attrs"
              >mdi-pencil</v-icon
            >
          </template>
          <span>{{ $t("actions.edit") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              @click="deleteItem(props.item)"
              :id="'environments__delete-' + props.index"
              :disabled="!isPermitted(PermissionEnum.ENVIRONMENTS.DELETE)"
              v-on="on"
              v-bind="attrs"
              >mdi-delete</v-icon
            >
          </template>
          <span>{{ $t("actions.delete") }}</span>
        </v-tooltip>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { handleError } from "@/utils/error";
import { isScrollBottom } from "@/utils";
import { permissionMixin } from "@/mixins/permissions";

export default {
  name: "EnvironmentsTable",
  mixins: [permissionMixin],
  data() {
    return {
      paginateParams: {
        object_id: 0,
        page_size: null,
        env_name: null
      },
      isLastPage: false,
      defaultPageSize: 50,
      height: 0,
      rows: 20,
      loadItems: [],
      options: {},
      loading: false,
      headers: [
        {
          text: this.$t("modules.environments.name"),
          align: "left",
          value: "name"
        },
        {
          text: this.$t("actions.actions"),
          align: "center",
          value: "action",
          sortable: false
        }
      ],
      editedIndex: -1
    };
  },

  computed: {
    dialog: {
      get() {
        return this.$store.state.environments.dialog;
      },
      set(val) {
        this.$store.dispatch("environments/toggleDialog", val);
      }
    },
    search: {
      get() {
        return this.$store.state.environments.search || "";
      },
      set(val) {
        this.$store.commit("environments/updateSearch", val);
      }
    },
    environments() {
      return this.$store.state.environments.environments;
    },
    editedItem() {
      return this.$store.state.environments.editedItem;
    },
    rowsCount() {
      return this.loadItems.length !== 0 && !this.isLastPage
        ? this.loadItems.length + "+"
        : this.loadItems.length;
    },
    hasChanges: {
      get() {
        return this.$store.state.environments.hasChanges;
      },
      set(val) {
        this.$store.commit("environments/updateChanges", val);
      }
    }
  },

  created() {
    if (this.search) {
      this.searchItems();
    } else {
      this.initialize();
    }
  },
  beforeDestroy() {
    document
      .querySelector(".v-data-table__wrapper")
      .removeEventListener("scroll", this.scroll);
  },
  mounted() {
    let el = document.querySelector(".v-data-table__wrapper");
    el.addEventListener("scroll", this.scroll);
    this.computeHeight();
  },

  watch: {
    search() {
      this.fetchEnviromentsDebounced();
    },
    hasChanges(val) {
      if (val) {
        this.refresh();
        this.hasChanges = false;
      }
    }
  },

  methods: {
    scroll({ target: { scrollTop, clientHeight, scrollHeight } }) {
      if (
        !this.isLastPage &&
        !this.loading &&
        isScrollBottom(scrollTop, clientHeight, scrollHeight)
      ) {
        this.paginate();
      }
    },
    computeHeight() {
      let el = this.$refs.dataContainer.$el;
      let boxOffset = el.getBoundingClientRect();
      let viewPortHeight = window.innerHeight;
      this.height = viewPortHeight - boxOffset.top;
    },
    paginate() {
      this.initialize();
    },
    initialize() {
      this.loading = true;
      this.$store
        .dispatch("environments/fetchAll", this.paginateParams)
        .then(() => {
          if (this.environments.length > 0) {
            this.loadItems = [...this.loadItems, ...this.environments];
            this.options.itemsPerPage = this.loadItems.length;
            if (this.loadItems.length === 0) {
              this.isLastPage = true;
              return;
            }
            this.paginateParams.object_id = this.loadItems[
              this.loadItems.length - 1
            ].id;
            this.isLastPage =
              this.environments.length < this.defaultPageSize ? true : false;
          } else {
            this.isLastPage = true;
          }
        })
        .catch(error => {
          handleError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    searchItems() {
      this.paginateParams = {
        object_id: 0,
        env_name: this.search !== "" ? this.search : null
      };

      this.loadItems = [];
      this.initialize();
    },
    fetchEnviromentsDebounced() {
      const debouceTimeoutInMilliseconds = 500;
      clearTimeout(this._searchTimerId);
      this._searchTimerId = setTimeout(() => {
        this.searchItems();
      }, debouceTimeoutInMilliseconds);
    },
    refresh() {
      this.paginateParams.object_id = 0;
      this.loadItems = [];
      this.initialize();
    },

    editItem(item) {
      let editedIndex = this.environments.indexOf(item);
      this.$store.commit("environments/updateFormTitle", 2);
      this.$store.commit("environments/updateIndex", editedIndex);
      this.$store.dispatch("environments/editItem", item);
      this.dialog = true;
    },

    deleteItem(item) {
      this.loading = true;
      let confirmed = confirm(this.$t("modules.environments.confirmDelete"));
      if (confirmed) {
        this.$store
          .dispatch("environments/delete", item.id)
          .then(() => {
            this.removeItem(item.id);
          })
          .catch(error => {
            handleError(error);
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        this.loading = false;
      }
    },
    removeItem(id) {
      let index = this.loadItems.findIndex(x => x.id === id);
      this.loadItems.splice(index, 1);
    }
  }
};
</script>

<style lang="scss" scoped>
$data-table-scroll-bar-width: 10px;
</style>
